import { useState, useEffect } from "react";
import { useSeo } from "@/hooks/useSeo";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";

interface Vacancy {
  id: number;
  title: string;
  description: string;
  requirements: string;
  salary: string;
}

const base = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function Vakansii() {
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Vacancy | null>(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", comment: "" });
  const [step, setStep] = useState<"list" | "form" | "sending" | "done" | "error">("list");
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: "Вакансии — Паруса на крыше",
    description: "Присоединяйтесь к команде ресторана Паруса на крыше. Открытые вакансии: официант, бармен, хостес и другие позиции.",
    canonical: "/vakansii",
  });
  useBreadcrumbs([{ name: "Вакансии", path: "/vakansii" }]);

  useEffect(() => {
    setTimeout(() => setLoaded(true), 80);
    fetch(`${base}/api/vacancies`)
      .then(r => r.json())
      .then(setVacancies)
      .catch(() => setVacancies([]))
      .finally(() => setLoading(false));
  }, []);

  async function handleApply() {
    if (!selected || !form.name.trim() || !form.phone.trim()) return;
    setStep("sending");
    try {
      const res = await fetch(`${base}/api/vacancies/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vacancyId: selected.id,
          vacancyTitle: selected.title,
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          comment: form.comment.trim(),
        }),
      });
      if (res.ok) { setStep("done"); }
      else { setStep("error"); }
    } catch { setStep("error"); }
  }

  const anim = {
    opacity: loaded ? 1 : 0,
    transform: loaded ? "translateY(0)" : "translateY(20px)",
    transition: "opacity 0.6s ease, transform 0.6s ease",
  };

  return (
    <div className="w-full min-h-screen bg-background relative z-10 pt-[100px] md:pt-[120px] pb-32 overflow-x-hidden">
      <div className="container mx-auto px-6 md:px-14 max-w-5xl relative">

        <div className="hidden sm:block absolute top-0 right-0 font-serif text-[110px] md:text-[210px] font-light leading-none text-primary/4 select-none pointer-events-none tracking-tighter -mt-20">Jobs</div>

        {/* Header */}
        <div className="mb-14 md:mb-20 relative z-10" style={anim}>
          <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-4">Работа у нас</div>
          <h1 className="text-4xl md:text-6xl font-serif font-light text-primary mb-6 tracking-wide leading-tight">
            Команда<br className="hidden md:block" /> Парусов
          </h1>
          <div className="w-24 h-[1px] bg-primary/30 mb-6" />
          <p className="text-sm text-muted-foreground font-light max-w-lg leading-relaxed">
            Мы — команда с характером. Работаем на крыше с видом на Петербург и строим сервис, которым гордимся. Если близко по духу — откликайтесь.
          </p>
        </div>

        {/* Done state */}
        {step === "done" && (
          <div className="border border-primary/30 bg-card/40 p-12 text-center max-w-xl" style={anim}>
            <div className="text-3xl mb-5">✓</div>
            <h2 className="font-serif text-2xl font-light text-primary mb-3">Отклик отправлен</h2>
            <p className="text-sm text-muted-foreground font-light leading-relaxed mb-8">
              Мы свяжемся с вами в ближайшее время. Спасибо за интерес к нашей команде!
            </p>
            <button
              onClick={() => { setStep("list"); setSelected(null); setForm({ name: "", phone: "", email: "", comment: "" }); }}
              className="border border-primary/40 text-accent px-8 py-3 text-[9px] tracking-[0.3em] uppercase hover:bg-primary/10 transition-colors"
            >
              К вакансиям
            </button>
          </div>
        )}

        {/* Vacancy list */}
        {step === "list" && (
          <div style={{ ...anim, transitionDelay: "0.1s" }}>
            {loading && (
              <div className="flex items-center gap-4 py-20">
                <div className="w-6 h-6 border border-primary/30 border-t-accent rounded-full animate-spin" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground/50">Загрузка…</span>
              </div>
            )}

            {!loading && vacancies.length === 0 && (
              <div className="border border-primary/15 p-16 text-center">
                <p className="text-sm text-muted-foreground font-light mb-4">Сейчас открытых вакансий нет.</p>
                <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground/40">Следите за обновлениями или напишите нам напрямую</p>
              </div>
            )}

            {!loading && vacancies.length > 0 && (
              <div className="space-y-4">
                {vacancies.map((v, i) => (
                  <div
                    key={v.id}
                    className="group border border-primary/15 hover:border-primary/40 transition-all cursor-pointer"
                    style={{ transitionDelay: `${i * 0.05}s` }}
                    onClick={() => { setSelected(v); setStep("form"); }}
                  >
                    <div className="p-7 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-4 mb-3">
                          <h2 className="font-serif text-2xl font-light text-primary group-hover:text-accent transition-colors">{v.title}</h2>
                          {v.salary && (
                            <span className="text-[9px] tracking-[0.25em] uppercase border border-primary/30 text-accent/80 px-2.5 py-1">
                              {v.salary}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground font-light leading-relaxed mb-0 line-clamp-2">{v.description}</p>
                        {v.requirements && (
                          <p className="mt-2 text-[11px] text-muted-foreground/50 font-light line-clamp-1">
                            <span className="text-primary/30 mr-1">Требования:</span>{v.requirements}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0">
                        <span className="text-[9px] tracking-[0.3em] uppercase text-primary/40 group-hover:text-accent transition-colors">
                          Откликнуться →
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Application form */}
        {(step === "form" || step === "sending" || step === "error") && selected && (
          <div className="border border-primary/20 bg-card/30 p-8 md:p-12 max-w-2xl" style={anim}>
            <button onClick={() => { setStep("list"); setSelected(null); }}
              className="text-[9px] tracking-[0.25em] uppercase text-muted-foreground/40 hover:text-primary mb-8 flex items-center gap-2 transition-colors">
              ← Все вакансии
            </button>

            <h2 className="font-serif text-2xl font-light text-primary mb-1">{selected.title}</h2>
            {selected.salary && (
              <p className="text-[10px] tracking-[0.2em] text-accent/60 uppercase mb-6">{selected.salary}</p>
            )}

            <div className="mb-8 space-y-3">
              <p className="text-sm text-muted-foreground font-light leading-relaxed">{selected.description}</p>
              {selected.requirements && (
                <div className="border-l-2 border-primary/20 pl-4">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-primary/40 mb-1">Требования</p>
                  <p className="text-sm text-muted-foreground/70 font-light">{selected.requirements}</p>
                </div>
              )}
            </div>

            <div className="border-t border-primary/10 pt-8">
              <h3 className="font-['Cormorant_SC'] text-sm tracking-[0.2em] text-primary/50 uppercase mb-6">Отклик на вакансию</h3>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60 mb-2">Имя *</label>
                    <input
                      value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="Иван Иванов"
                      className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground focus:outline-none focus:border-primary transition-colors text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60 mb-2">Телефон *</label>
                    <input
                      type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                      placeholder="+7 (___) ___-__-__"
                      className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground focus:outline-none focus:border-primary transition-colors text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60 mb-2">Email (необязательно)</label>
                  <input
                    type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="ivan@example.com"
                    className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground focus:outline-none focus:border-primary transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60 mb-2">О себе (необязательно)</label>
                  <textarea rows={3} value={form.comment} onChange={e => setForm({ ...form, comment: e.target.value })}
                    placeholder="Опыт работы, пожелания по графику, ссылка на резюме…"
                    className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground focus:outline-none focus:border-primary transition-colors text-sm resize-none"
                  />
                </div>

                {step === "error" && (
                  <p className="text-red-400 text-sm">Ошибка отправки. Попробуйте ещё раз.</p>
                )}

                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <button
                    onClick={handleApply}
                    disabled={!form.name.trim() || !form.phone.trim() || step === "sending"}
                    className="flex-1 bg-primary/20 border border-primary text-accent hover:bg-primary hover:text-background disabled:opacity-40 disabled:cursor-not-allowed py-4 text-[10px] tracking-[0.3em] uppercase font-medium transition-colors"
                  >
                    {step === "sending" ? "Отправка…" : "Отправить отклик"}
                  </button>
                  <button onClick={() => { setStep("list"); setSelected(null); }}
                    className="border border-primary/20 text-muted-foreground px-8 py-4 text-[9px] tracking-[0.2em] uppercase hover:text-primary transition-colors">
                    Назад
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Values block */}
        {step === "list" && !loading && (
          <div className="mt-20 border-t border-primary/10 pt-16" style={{ ...anim, transitionDelay: "0.2s" }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {[
                { title: "Атмосфера", text: "Работа с видом на исторический Петербург. Не просто ресторан — место с характером." },
                { title: "Команда", text: "Небольшой сплочённый коллектив. Каждый важен, голос слышат." },
                { title: "Рост", text: "Внутреннее обучение, гибкий график, карьерное развитие для тех, кто хочет большего." },
              ].map(item => (
                <div key={item.title}>
                  <div className="font-['Cormorant_SC'] text-xs tracking-[0.25em] text-primary/50 uppercase mb-3">{item.title}</div>
                  <p className="text-sm text-muted-foreground font-light leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
